import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { RootState } from 'dataStore/state'
import { isIosSafariNotStandalone } from 'handlers/isIosSafariNotStandalone'
import { requestNotificationPermission } from 'handlers/showNewOrderNotification'
import { subscribeAdminPush } from 'handlers/subscribeAdminPush'

import { NotificationsIconButton } from './EnableNotificationsButton.styles'

/**
 * Кнопка в хедере: Browser Notification + подписка Web Push.
 */
const EnableNotificationsButton: FC = () => {
  const filterPizzeriaId = useSelector(
    (state: RootState) => state.orders.filter?.pizzeria_id ?? null
  )
  const [permission, setPermission] = useState<NotificationPermission>(() => {
    if (typeof Notification === 'undefined') {
      return 'denied'
    }
    return Notification.permission
  })

  useEffect(() => {
    if (typeof Notification === 'undefined') {
      return
    }
    setPermission(Notification.permission)
  }, [])

  /**
   * Запрашивает permission, подписывает на push, показывает toast.
   */
  const handleEnableNotifications = async (): Promise<void> => {
    if (typeof Notification === 'undefined') {
      toast.error('Уведомления не поддерживаются в этом браузере')
      return
    }

    if (isIosSafariNotStandalone()) {
      toast.info(
        'На iPhone: Поделиться → На экран «Домой», затем откройте приложение и включите уведомления снова.'
      )
    }

    const nextPermission = await requestNotificationPermission()
    setPermission(nextPermission)

    if (nextPermission === 'granted') {
      try {
        await subscribeAdminPush(
          filterPizzeriaId == null ? null : Number(filterPizzeriaId)
        )
        toast.success('Уведомления о новых заказах включены (включая фон)')
      } catch (error: any) {
        console.error(error)
        toast.warning(
          error?.message ||
            'Разрешение получено, но фоновый Web Push не удалось включить'
        )
      }
      return
    }

    if (nextPermission === 'denied') {
      toast.error(
        'Уведомления запрещены. Разрешите их в настройках браузера.'
      )
      return
    }

    toast.info('Разрешение на уведомления не получено')
  }

  const NotificationStateIcon =
    permission === 'granted'
      ? NotificationsActiveIcon
      : permission === 'denied'
        ? NotificationsOffIcon
        : NotificationsIcon

  return (
    <NotificationsIconButton
      color='inherit'
      aria-label='Включить уведомления о новых заказах'
      title='Уведомления о новых заказах'
      onClick={() => {
        void handleEnableNotifications()
      }}
    >
      <NotificationStateIcon />
    </NotificationsIconButton>
  )
}

export default EnableNotificationsButton
